import { DEFAULT_GROUPPED_LINKS_BY_BRAND } from "@/lib/constants";
import processDelta, { replaceIdInText } from "@/lib/processDelta";
import { EUserInfoFormStatus, type HistoryItem } from "@/types";
import { TInfoBox } from "@/types/TInfoBox";
import { EMicStatus, TMicStatus } from "@/types/TMicStatus";
import { TUser } from "@/types/TUser";
import { TBrokerInfo } from "@/types/TBrokerInfo";
import { TClaimStatusInfo } from "@/types/TClaimStatusInfo";
import { create } from "zustand";

export type TAvatarStatus = "Bereit" | "Anhören" | "Sprechen" | "Denken" | "Frage Blockbrain" | "Warten auf Ihren Beitrag" | "";

export type ChatState = {
  avatarStatus: TAvatarStatus;
  setAvatarStatus: (status: TAvatarStatus) => void;
  isAvatarReady: boolean;
  setIsAvatarReady: (isReady: boolean) => void;
  isAvatarSpeaking: boolean;
  setIsAvatarSpeaking: (isSpeaking: boolean) => void;
  isHistoryPanelOpen: boolean;
  setIsHistoryPanelOpen: (isPanelOpen: boolean) => void;
  micStatus: TMicStatus;
  setMicStatus: (micStatus: TMicStatus) => void;
  userInfoFormStatus: EUserInfoFormStatus;
  setUserInfoFormStatus: (status: EUserInfoFormStatus) => void;
  userInfoFormErrors: Record<string, string>;
  setUserInfoFormError: (field: string, error: string) => void;
  clearUserInfoFormErrors: () => void;
  isRunning: boolean;
  setIsRunning: (isRunning: boolean) => void;
  messages: HistoryItem[];
  addMessage: (message: HistoryItem) => void;
  resetMessages: () => void;
  updateLastMessageTranscript: (message: string, sender: "user" | "assistant") => void;
  updateLastMessageGroundingFiles: (groundingFiles: any[]) => void;
  user: TUser | undefined;
  setUser: (user?: TUser) => void;
  deltaBuffer: string[];
  appendDeltaBuffer: (deltaBuffer: string) => void;
  resetDeltaBuffer: () => void;
  isForcedToInterrupt: boolean;
  setIsForcedToInterrupt: (isForcedToInterrupt: boolean) => void;
  responseSessionId: number;
  incrementResponseSessionId: () => void;
  displayedExternalLinks: TInfoBox[];
  setDisplayedExternalLinks: (links: TInfoBox[]) => void;
  abortControllers: AbortController[];
  registerAbortController: (controller: AbortController) => void;
  clearAbortControllers: () => void;
  sessionId: string | undefined;
  setSessionId: (sessionId: string) => void;
  // input mode toggle (voice vs text)
  inputMode: "voice" | "text";
  setInputMode: (mode: "voice" | "text") => void;
  // send text helper (wired by component with realtime hook)
  sendText?: (text: string) => void;
  // trigger a response from the Realtime API for the existing conversation
  // (used to bypass SchadenKI routing for tool-only responses, e.g. Blockbrain)
  triggerRealtimeResponse?: () => void;
  // True while the user is in a continuing Blockbrain conversation. Used so
  // follow-up questions without the trigger word still go to the Realtime API
  // (and therefore to the ask_blockbrain tool) instead of SchadenKI.
  inBlockbrainConversation: boolean;
  setInBlockbrainConversation: (active: boolean) => void;
  // True between the moment a Blockbrain query is dispatched and the first
  // assistant audio/text token arrives. Drives a loading bubble in the chat
  // panel and the "Frage Blockbrain" status in the avatar window.
  isWaitingForBlockbrain: boolean;
  setIsWaitingForBlockbrain: (waiting: boolean) => void;
  // broker validation dialog
  brokerValidationDialogOpen: boolean;
  setBrokerValidationDialogOpen: (open: boolean) => void;
  brokerInfo: TBrokerInfo | null;
  setBrokerInfo: (info: TBrokerInfo | null) => void;
  // claim status dialog
  claimStatusDialogOpen: boolean;
  setClaimStatusDialogOpen: (open: boolean) => void;
  claimStatusInfo: TClaimStatusInfo | null;
  setClaimStatusInfo: (info: TClaimStatusInfo | null) => void;
};

export const ChatStore = create<ChatState>((set, get) => ({
  avatarStatus: "",
  isAvatarReady: false,
  isRunning: false,
  micStatus: EMicStatus.enabled,
  inputMode: "voice",
  messages: [],
  isHistoryPanelOpen: false,
  userInfoFormStatus: EUserInfoFormStatus.hidden,
  userInfoFormErrors: {},
  user: undefined,
  deltaBuffer: [],
  isForcedToInterrupt: false,
  responseSessionId: 0,
  displayedExternalLinks: DEFAULT_GROUPPED_LINKS_BY_BRAND,
  abortControllers: [],
  isAvatarSpeaking: false,
  setIsAvatarSpeaking: (isAvatarSpeaking: boolean) => set({ isAvatarSpeaking }),
  setIsForcedToInterrupt: (isForcedToInterrupt: boolean) => {
    if (isForcedToInterrupt) {
      get().abortControllers.forEach(controller => {
        try {
          controller.abort();
        } catch (error) {
          console.error('Error aborting request:', error);
        }
      });
      set({ abortControllers: [] });
    }
    set({ isForcedToInterrupt });
  },
  registerAbortController: (controller: AbortController) => {
    set(state => ({ 
      abortControllers: [...state.abortControllers, controller]
    }));
  },
  clearAbortControllers: () => set({ abortControllers: [] }),
  // sendText will be injected by component that owns the realtime hook
  sendText: undefined,
  triggerRealtimeResponse: undefined,
  inBlockbrainConversation: false,
  setInBlockbrainConversation: (active: boolean) => set({ inBlockbrainConversation: active }),
  isWaitingForBlockbrain: false,
  setIsWaitingForBlockbrain: (waiting: boolean) => set({ isWaitingForBlockbrain: waiting }),
  setUser: user => set({ user }),
  setIsAvatarReady: (isReady: boolean) => set({ isAvatarReady: isReady }),
  setAvatarStatus: (status: TAvatarStatus) => set({ avatarStatus: status }),
  setIsRunning: (isRunning: boolean) => set({ isRunning }),
  addMessage: (message: HistoryItem) => {
    const messages = get().messages;
    set({ messages: [...messages, message] });
  },
  updateLastMessageTranscript: (delta: string, sender: "user" | "assistant") => {
    const messages = get().messages;
    const lastMessage = messages.findLast(m => m.sender === sender);
    if (!lastMessage) {
      console.error("No last message found for ", sender);
      return;
    }
    const newTranscript = replaceIdInText(lastMessage.transcript + delta);
    lastMessage.transcript = newTranscript;
    set({ messages: [...messages] });
  },
  updateLastMessageGroundingFiles: groundingFiles => {
    const messages = get().messages;
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage) {
      console.error("No last message found for grounding files");
      return;
    } else if (lastMessage.sender === "user") {
      console.error("Last message is from user, cannot add grounding files");
      return;
    }
    messages[messages.length - 1].groundingFiles = groundingFiles;
    set({ messages: [...messages] });
  },
  resetMessages: () => set({ 
    messages: [], 
    displayedExternalLinks: DEFAULT_GROUPPED_LINKS_BY_BRAND, 
    sessionId: undefined,
    brokerInfo: null,
    brokerValidationDialogOpen: false,
    claimStatusInfo: null,
    claimStatusDialogOpen: false
  }),
  setMicStatus: (micStatus: TMicStatus) => set({ micStatus }),
  setUserInfoFormStatus: (status: EUserInfoFormStatus) => set({ userInfoFormStatus: status }),
  setIsHistoryPanelOpen: (isHistoryPanelOpen: boolean) => set({ isHistoryPanelOpen }),
  appendDeltaBuffer: (newDelta: string) => {
    const currentDeltaBuffer = get().deltaBuffer;
    const updatedDeltaBuffer = processDelta(newDelta, currentDeltaBuffer);
    set({ deltaBuffer: updatedDeltaBuffer });
  },
  resetDeltaBuffer: () => set({ deltaBuffer: [] }),
  incrementResponseSessionId: () => set(state => ({ responseSessionId: state.responseSessionId + 1 })),
  setDisplayedExternalLinks: (displayedExternalLinks: TInfoBox[]) => set({ displayedExternalLinks }),
  setUserInfoFormError: (field: string, error: string) => set(state => ({ 
    userInfoFormErrors: { ...state.userInfoFormErrors, [field]: error }
  })),
  clearUserInfoFormErrors: () => set({ userInfoFormErrors: {} }),
  sessionId: undefined,
  setSessionId: (sessionId: string) => set({ sessionId }),
  setInputMode: (mode: "voice" | "text") => set({ inputMode: mode }),
  brokerValidationDialogOpen: false,
  setBrokerValidationDialogOpen: (open: boolean) => set({ brokerValidationDialogOpen: open }),
  brokerInfo: null,
  setBrokerInfo: (info: TBrokerInfo | null) => set({ brokerInfo: info }),
  claimStatusDialogOpen: false,
  setClaimStatusDialogOpen: (open: boolean) => set({ claimStatusDialogOpen: open }),
  claimStatusInfo: null,
  setClaimStatusInfo: (info: TClaimStatusInfo | null) => set({ claimStatusInfo: info }),
}));
